import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Order, fetchOrders } from '../api/orderApi';
import { toast } from 'react-toastify';
import { playNotificationSound } from '../utils/playNotificationSound';
import { showNewOrderPopup } from '../components/NewOrderNotification';
import { useAuth } from './useAuth';
import { useOrderNotifications } from '../context/OrderNotificationContext';

const SOCKET_URL = 'https://api.thunderbirdsmoke.net';
const LOCAL_KNOWN_KEY = 'fc_known_orders';

export const useOrderSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const { user } = useAuth();
  const { addUnread } = useOrderNotifications();
  const knownOrders = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!user) return;

    // Load known orders from localStorage
    const saved = localStorage.getItem(LOCAL_KNOWN_KEY);
    if (saved) {
      try {
        knownOrders.current = new Set(JSON.parse(saved));
      } catch (e) {
        console.warn('Failed to parse localStorage known orders', e);
      }
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    const persistKnownOrders = () => {
      localStorage.setItem(LOCAL_KNOWN_KEY, JSON.stringify(Array.from(knownOrders.current)));
    };

    const handleOrder = (data: Order) => {
      if (!knownOrders.current.has(data.order_id)) {
        console.log('🔔 New order received:', data.order_id);

        knownOrders.current.add(data.order_id);
        persistKnownOrders();

        const soundAllowed = localStorage.getItem('soundPermission') === 'granted';
        if (soundAllowed) {
          playNotificationSound();
        }

        toast.info(`New Online Order #${data.order_id}`, {
          autoClose: 5000,
          position: 'bottom-right',
        });

        showNewOrderPopup(data);
        addUnread();
      }
    };

    const fetchRecentOrders = async () => {
      try {
        const recent = await fetchOrders({ source: 'online', limit: 10 });
        recent.forEach((order) => {
          if (!knownOrders.current.has(order.order_id)) {
            handleOrder(order);
          }
        });
      } catch (err) {
        console.error('Failed to fetch recent orders:', err);
      }
    };

    socket.on('connect', () => {
      console.log('🟢 Socket connected');
      fetchRecentOrders();
    });

    socket.on('newOrder', handleOrder);

    socket.on('disconnect', () => {
      console.warn('🔌 Disconnected from socket');
    });

    socket.on('connect_error', (err) => {
      console.error('❌ Socket connection error:', err);
    });

    return () => {
      socket.disconnect();
    };
  }, [user, addUnread]);
};
